import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { LoopIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import type { EnrichedReadyToSharePlatformPostV2 } from '#src/app/shared/api/readyToShare';

import type { PostPreviewSizes, RetryState } from '../../types';

type RetryOverlayProps = {
	size: PostPreviewSizes;
	retryState: RetryState;
	variant: EnrichedReadyToSharePlatformPostV2['__typename'];
	onRetry?: () => void;
};

const textStyles: Record<PostPreviewSizes, string> = {
	xsmall: 'text-size-caption',
	small: 'text-size-body-small',
	medium: 'text-size-body-small',
	large: 'text-size-body',
};

export const RetryOverlay: React.FC<RetryOverlayProps> = ({ size, retryState, variant, onRetry }) => {
	// When retrying, don't render the overlay - just show the loading skeleton underneath
	if (retryState === 'retrying') {
		return null;
	}

	// Story variants have dark backgrounds, so use white text for better contrast
	const isStoryVariant = variant === 'ReadyToShareFacebookStory' || variant === 'ReadyToShareInstagramStory';
	const textColorClass = isStoryVariant ? 'text-white' : 'text-black';

	return (
		<div
			data-testid="retry-overlay"
			data-qa="retry-overlay"
			className="absolute inset-0 z-5 flex cursor-pointer flex-col items-center justify-center rounded-lg"
			onClick={onRetry}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					onRetry?.();
				}
			}}
			aria-label="Text not generated. Click to refresh"
		>
			<Text
				as="span"
				className={cn('mb-2 text-center font-medium', textColorClass, textStyles[size])}
				data-testid="retry-overlay-message"
			>
				Text not generated.
				<br />
				Click to refresh
			</Text>
			<LoopIcon className={textColorClass} size="large" data-testid="retry-overlay-icon" aria-hidden="true" />
		</div>
	);
};
